import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

// Type → confirmation copy bank. One random line picked per save so
// returning users see variety. Strictly verbatim from the product spec.
const CAPTURE_CONFIRMATIONS = {
    TASK: [
        "got it — added to your tasks 🍡",
        "task saved. mochi's on it.",
    ],
    HABIT: [
        "new habit noted 🌱 let's build it together.",
        "added to habits. small steps 🍡",
    ],
    REMINDER: [
        "i'll remind you 🔔 mochi won't forget.",
        "reminder set. i've got your back 🍡",
    ],
    LIST: [
        "on the list ✓ mochi is an excellent personal shopper.",
        "noted 🍡 added to your list.",
    ],
};
const TYPE_TO_TAB_ID = { TASK: "task", HABIT: "habit", REMINDER: "reminder", LIST: "list" };

// Global quick-capture (Cmd+K / mobile floating button). Distinct from
// QuickAddSheet — that's the explicit per-type wizard, this is the
// "type anything, Claude sorts it" instant capture.
// `tabPulse` is a brief tab-pulse trigger set after a successful capture so
// the user sees exactly where their item landed: { tab, n } where `n` is a
// monotonically incrementing nonce so the animation re-fires.
export function useQuickCaptureFlow({ loadAll, refreshStats, mood, setViewMode, setActiveTab }) {
    const [captureOpen, setCaptureOpen] = useState(false);
    const [tabPulse, setTabPulse] = useState({ tab: null, n: 0 });

    // Cmd+K / Ctrl+K — explicitly suppressed when the user is typing in
    // another input so we never steal focus mid-edit.
    useEffect(() => {
        const onKey = (e) => {
            if (!(e.metaKey || e.ctrlKey) || (e.key !== "k" && e.key !== "K")) return;
            const el = document.activeElement;
            const tag = el?.tagName?.toLowerCase();
            const typing = tag === "input" || tag === "textarea" || el?.isContentEditable;
            if (typing) return;
            e.preventDefault();
            setCaptureOpen(true);
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, []);

    const handleCaptured = useCallback(async (res) => {
        if (!res?.type) return;
        const tabId = TYPE_TO_TAB_ID[res.type] || "task";
        // Refresh ALL collections — cheapest path, and the capture endpoint
        // doesn't tell us which list to refresh otherwise.
        try {
            await loadAll();
        } catch (e) {
            console.warn("capture: post-save refresh failed", e);
        }
        // Jump back to list view so the user can see where the item landed.
        setViewMode("list");
        setActiveTab(tabId);
        setTabPulse((p) => ({ tab: tabId, n: p.n + 1 }));
        setTimeout(() => setTabPulse({ tab: null, n: 0 }), 1600);
        // Pick the right Mochi line.
        let line;
        if (res.ambiguous) {
            line = "i wasn't totally sure, so i added it as a task — you can move it if you want 🍡";
        } else if (res.truncated) {
            line = "that was a lot — i saved the important part 🍡";
        } else {
            const pool = CAPTURE_CONFIRMATIONS[res.type] || CAPTURE_CONFIRMATIONS.TASK;
            line = pool[Math.floor(Math.random() * pool.length)];
        }
        // Surface in Mochi's bubble for ~4s, then let the normal mood take back over.
        mood.setBubbleMsg?.(line);
        mood.forceMoodFor?.("happy", 3000);
        toast(line, { duration: 3000 });
        refreshStats?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadAll, refreshStats, setViewMode, setActiveTab]);

    return { captureOpen, setCaptureOpen, tabPulse, handleCaptured };
}
